import React, { useState, useEffect, useCallback } from 'react';
import './AdminDashboard.css';
import AdminProgramManagement from './AdminProgramManagement';
import AdminEnrollmentManagement from './AdminEnrollmentManagement';
import AdminQuestionManagement from './AdminQuestionManagement';
import AdminAssessmentManagement from './AdminAssessmentManagement';
import config from '../config';

const { API_URL } = config;

const AdminDashboard = ({ user, onBack, onMessage, onEnrollmentUpdate, refreshEnrollments }) => {
  const [activeSection, setActiveSection] = useState('overview');


  // Admin menu items
  const menuItems = [
    {
      id: 'overview',
      label: 'Overview',
      description: 'Dashboard and statistics'
    },
    {
      id: 'programs',
      label: 'Program Management',
      description: 'Create and manage certification programs'
    },
    {
      id: 'assessments',
      label: 'Assessment Management',
      description: 'Configure assessments and scoring'
    },
    {
      id: 'questions',
      label: 'Question Management',
      description: 'Add and edit assessment questions'
    },
    {
      id: 'enrollments',
      label: 'Enrollment Management',
      description: 'Manage user enrollments and access'
    },
    {
      id: 'results',
      label: 'Exam Results & Management',
      description: 'View exam results, analytics, and manage user attempts'
    }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return <AdminOverview user={user} onMessage={onMessage} setActiveSection={setActiveSection} />;
      
      case 'programs':
        return (
          <AdminProgramManagement
            user={user}
            onMessage={onMessage}
          />
        );
      
      case 'assessments':
        return (
          <AdminAssessmentManagement
            user={user}
            onMessage={onMessage}
          />
        );
      
      case 'questions':
        return <AdminQuestionManagement user={user} onMessage={onMessage} />;
      
      case 'enrollments':
        return (
          <AdminEnrollmentManagement
            user={user}
            onMessage={onMessage}
            onEnrollmentUpdate={onEnrollmentUpdate}
            refreshEnrollments={refreshEnrollments}
          />
        );
      
      case 'results':
        return (
          <AdminExamResultsManagement
            user={user}
            onMessage={onMessage}
          />
        );
      
      default:
        return <AdminOverview user={user} onMessage={onMessage} />;
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Administration Dashboard</h1>
        <button className="btn btn-secondary" onClick={onBack}>
          ← Back to Main
        </button>
      </div>

      <div className="admin-layout">
        {/* Left Sidebar Menu */}
        <div className="admin-sidebar">
          <div className="admin-user-info">
            <div className="admin-user-details">
              <div className="admin-user-name">{user?.email}</div>
              <div className="admin-user-role">Administrator</div>
            </div>
          </div>

          <nav className="admin-nav">
            <div className="admin-nav-section">
              <div className="admin-nav-title">Administration</div>
              {menuItems.map(item => (
                <button
                  key={item.id}
                  className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                  title={item.description}
                >
                  <span className="admin-nav-label">{item.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="admin-content">
          <div className="admin-content-body">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for sections not yet implemented
const AdminOverview = ({ user, onMessage, setActiveSection }) => {
  const [stats, setStats] = useState({
    programs: { count: 0, loading: true },
    enrollments: { count: 0, loading: true },
    assessments: { count: 0, loading: true },
    successRate: { rate: 0, loading: true }
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    loadOverviewData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOverviewData = async () => {
    try {
      // Load programs count
      loadProgramsCount();
      
      // Load enrollments count
      loadEnrollmentsCount();
      
      // Load assessments count
      loadAssessmentsCount();
      
      // Load success rate
      loadSuccessRate();
      
      // Load recent activity
      loadRecentActivity();
    } catch (error) {
      console.error('Error loading overview data:', error);
    }
  };

  const loadProgramsCount = async () => {
    try {
      // Demo mode - provide demo count
      if (user?.token === 'demo-token-for-local-testing') {
        setStats(prev => ({ ...prev, programs: { count: 3, loading: false } }));
        return;
      }

      const response = await fetch(`${API_URL}/admin/programs`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const programs = await response.json();
        setStats(prev => ({ 
          ...prev, 
          programs: { count: Array.isArray(programs) ? programs.length : 0, loading: false }
        }));
      } else {
        setStats(prev => ({ ...prev, programs: { count: 0, loading: false } }));
      }
    } catch (error) {
      console.error('Error loading programs:', error);
      setStats(prev => ({ ...prev, programs: { count: 0, loading: false } }));
    }
  };

  const loadEnrollmentsCount = async () => {
    try {
      // Demo mode - provide demo count
      if (user?.token === 'demo-token-for-local-testing') {
        setStats(prev => ({ ...prev, enrollments: { count: 2, loading: false } }));
        return;
      }

      const response = await fetch(`${API_URL}/admin/enrollments`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const enrollments = await response.json();
        console.log('DEBUG - Enrollments data:', enrollments);
        console.log('DEBUG - Is array:', Array.isArray(enrollments));
        console.log('DEBUG - Total enrollments:', enrollments?.length);
        
        // Log sample enrollment to see structure
        if (Array.isArray(enrollments) && enrollments.length > 0) {
          console.log('DEBUG - Sample enrollment:', enrollments[0]);
          console.log('DEBUG - Status values:', enrollments.map(e => e.status));
        }
        
        const activeEnrollments = Array.isArray(enrollments) 
          ? enrollments.filter(e => e.status === 'active').length 
          : 0;
        console.log('DEBUG - Active enrollments count:', activeEnrollments);
        
        setStats(prev => ({ 
          ...prev, 
          enrollments: { count: activeEnrollments, loading: false }
        }));
      } else {
        console.error('DEBUG - Enrollments response not OK:', response.status);
        setStats(prev => ({ ...prev, enrollments: { count: 0, loading: false } }));
      }
    } catch (error) {
      console.error('Error loading enrollments:', error);
      setStats(prev => ({ ...prev, enrollments: { count: 0, loading: false } }));
    }
  };

  const loadAssessmentsCount = async () => {
    try {
      // Demo mode - provide demo count
      if (user?.token === 'demo-token-for-local-testing') {
        setStats(prev => ({ ...prev, assessments: { count: 3, loading: false } }));
        return;
      }

      const response = await fetch(`${API_URL}/admin/questions?type=assessments`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const assessments = await response.json();
        setStats(prev => ({ 
          ...prev, 
          assessments: { count: Array.isArray(assessments) ? assessments.length : 0, loading: false }
        }));
      } else {
        setStats(prev => ({ ...prev, assessments: { count: 0, loading: false } }));
      }
    } catch (error) {
      console.error('Error loading assessments:', error);
      setStats(prev => ({ ...prev, assessments: { count: 0, loading: false } }));
    }
  };

  const loadSuccessRate = async () => {
    try {
      // Demo mode - provide demo rate
      if (user?.token === 'demo-token-for-local-testing') {
        setStats(prev => ({ ...prev, successRate: { rate: 78, loading: false } }));
        return;
      }

      const response = await fetch(`${API_URL}/admin/questions?type=attempts`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const attempts = await response.json();
        console.log('DEBUG - Attempts data:', attempts);
        console.log('DEBUG - Is array:', Array.isArray(attempts));
        console.log('DEBUG - Total attempts:', attempts?.length);
        
        // Log sample attempt to see structure
        if (Array.isArray(attempts) && attempts.length > 0) {
          console.log('DEBUG - Sample attempt:', attempts[0]);
          console.log('DEBUG - Passed values:', attempts.map(a => a.passed));
        }
        
        if (Array.isArray(attempts) && attempts.length > 0) {
          const passedAttempts = attempts.filter(a => a.passed).length;
          const successRate = Math.round((passedAttempts / attempts.length) * 100);
          console.log('DEBUG - Passed attempts:', passedAttempts);
          console.log('DEBUG - Success rate:', successRate);
          
          setStats(prev => ({ 
            ...prev, 
            successRate: { rate: successRate, loading: false }
          }));
        } else {
          console.log('DEBUG - No attempts found or not an array');
          setStats(prev => ({ ...prev, successRate: { rate: 0, loading: false } }));
        }
      } else {
        console.error('DEBUG - Attempts response not OK:', response.status);
        setStats(prev => ({ ...prev, successRate: { rate: 0, loading: false } }));
      }
    } catch (error) {
      console.error('Error loading success rate:', error);
      setStats(prev => ({ ...prev, successRate: { rate: 0, loading: false } }));
    }
  };

  const loadRecentActivity = async () => {
    try {
      // Demo mode - provide demo activity
      if (user?.token === 'demo-token-for-local-testing') {
        setRecentActivity([
          { text: 'Demo user completed Cloud Operator assessment', time: '2 hours ago' },
          { text: 'New demo enrollment created', time: '4 hours ago' },
          { text: 'Demo questions updated', time: '1 day ago' }
        ]);
        setActivityLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/admin/questions?type=attempts&limit=5`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const attempts = await response.json();
        if (Array.isArray(attempts)) {
          const activities = attempts.slice(0, 3).map(attempt => ({
            text: `${attempt.user_email || 'User'} ${attempt.passed ? 'passed' : 'failed'} ${attempt.assessment_name || 'assessment'}`,
            time: attempt.timestamp ? formatTimeAgo(parseInt(attempt.timestamp) * 1000) : 'Recently'
          }));
          setRecentActivity(activities);
        }
      }
      setActivityLoading(false);
    } catch (error) {
      console.error('Error loading recent activity:', error);
      setRecentActivity([]);
      setActivityLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return 'Recently';
  };

  return (
    <div className="admin-overview">
      <div className="overview-cards">
        <div className="overview-card">
          <div className="overview-card-content">
            <h3>Programs</h3>
            <p>Manage certification programs and requirements</p>
            <div className="overview-card-stat">
              {stats.programs.loading ? 'Loading...' : `${stats.programs.count} Active Program${stats.programs.count !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
        
        <div className="overview-card">
          <div className="overview-card-content">
            <h3>Enrollments</h3>
            <p>Monitor user enrollments and progress</p>
            <div className="overview-card-stat">
              {stats.enrollments.loading ? 'Loading...' : `${stats.enrollments.count} Active Enrollment${stats.enrollments.count !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
        
        <div className="overview-card">
          <div className="overview-card-content">
            <h3>Assessments</h3>
            <p>Configure assessments and questions</p>
            <div className="overview-card-stat">
              {stats.assessments.loading ? 'Loading...' : `${stats.assessments.count} Assessment${stats.assessments.count !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
        
        <div className="overview-card">
          <div className="overview-card-content">
            <h3>Success Rate</h3>
            <p>Overall assessment pass rate</p>
            <div className="overview-card-stat">
              {stats.successRate.loading ? 'Loading...' : `${stats.successRate.rate}%`}
            </div>
          </div>
        </div>
      </div>

      <div className="overview-section">
        <h3>Quick Actions</h3>
        <div className="quick-actions">
          <button className="btn btn-primary" onClick={() => setActiveSection('programs')}>
            Add New Program
          </button>
          <button className="btn btn-primary" onClick={() => setActiveSection('assessments')}>
            Create Assessment
          </button>
          <button className="btn btn-primary" onClick={() => setActiveSection('results')}>
            View Results & Analytics
          </button>
          <button className="btn btn-primary" onClick={() => setActiveSection('enrollments')}>
            Manage Enrollments
          </button>
        </div>
      </div>

      <div className="overview-section">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {activityLoading ? (
            <div className="activity-item">
              <span className="activity-text">Loading recent activity...</span>
            </div>
          ) : recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <span className="activity-text">{activity.text}</span>
                <span className="activity-time">{activity.time}</span>
              </div>
            ))
          ) : (
            <div className="activity-item">
              <span className="activity-text">No recent activity</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const AdminExamResultsManagement = ({ user, onBack, onMessage }) => {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmail, setFilterEmail] = useState('');
  const [filterAssessment, setFilterAssessment] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('analytics');

  const loadAttempts = useCallback(async () => {
    setLoading(true);
    try {
      // Use the admin questions endpoint with type=attempts parameter
      const url = `${API_URL}/admin/questions?type=attempts`;
      const response = await fetch(url, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      
      // Filter by email and assessment if specified
      let filteredAttempts = data || [];
      if (filterEmail) {
        filteredAttempts = filteredAttempts.filter(attempt => 
          attempt.user_email && attempt.user_email.toLowerCase().includes(filterEmail.toLowerCase())
        );
      }
      if (filterAssessment) {
        filteredAttempts = filteredAttempts.filter(attempt => 
          attempt.assessment_name && attempt.assessment_name.toLowerCase().includes(filterAssessment.toLowerCase())
        );
      }
      
      // Sort by timestamp descending
      const sortedAttempts = filteredAttempts.sort((a, b) => {
        const aTime = parseInt(a.timestamp) || 0;
        const bTime = parseInt(b.timestamp) || 0;
        return bTime - aTime;
      });
      
      setAttempts(sortedAttempts);
    } catch (error) {
      console.error('Error loading attempts:', error);
      onMessage({ text: 'Error loading attempts: ' + error.message, type: 'error' });
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }, [filterEmail, filterAssessment, user?.token, onMessage]);

  useEffect(() => {
    loadAttempts();
  }, [loadAttempts]);

  const handleResetAttempt = async (userEmail, assessmentName) => {
    if (!window.confirm(`Reset attempt for ${userEmail} on ${assessmentName}?\n\nThis will allow them to retake the assessment.\n\nNote: The user will need to refresh their browser to see the change.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/reset-attempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({
          user_email: userEmail,
          assessment_name: assessmentName
        })
      });

      const result = await response.json();

      if (response.ok) {
        onMessage({ text: `Successfully reset attempt for ${userEmail}. User needs to refresh their browser.`, type: 'success' });
        loadAttempts(); // Reload the list
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Unknown error'), type: 'error' });
      }
    } catch (error) {
      console.error('Error resetting attempt:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(parseInt(timestamp) * 1000);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  // Calculate analytics data
  const totalAttempts = attempts.length;
  const passedAttempts = attempts.filter(a => a.passed).length;
  const failedAttempts = totalAttempts - passedAttempts;
  const averageScore = totalAttempts > 0 ? 
    (attempts.reduce((sum, a) => sum + (a.score || 0), 0) / totalAttempts).toFixed(1) : 0;

  // Get unique users and their results
  const userResults = {};
  attempts.forEach(attempt => {
    const userEmail = attempt.user_email || 'Anonymous';
    if (!userResults[userEmail]) {
      userResults[userEmail] = {
        email: userEmail,
        attempts: [],
        bestScore: 0,
        totalAttempts: 0,
        passed: false
      };
    }
    userResults[userEmail].attempts.push(attempt);
    userResults[userEmail].totalAttempts++;
    userResults[userEmail].bestScore = Math.max(userResults[userEmail].bestScore, attempt.score || 0);
    if (attempt.passed) {
      userResults[userEmail].passed = true;
    }
  });

  const uniqueUsers = Object.values(userResults);
  const passedUsers = uniqueUsers.filter(u => u.passed).length;
  const failedUsers = uniqueUsers.filter(u => !u.passed).length;

  // Filter and sort attempts for detailed view
  let filteredAttempts = attempts;
  if (filterStatus === 'passed') {
    filteredAttempts = attempts.filter(a => a.passed);
  } else if (filterStatus === 'failed') {
    filteredAttempts = attempts.filter(a => !a.passed);
  }

  // Sort attempts
  filteredAttempts.sort((a, b) => {
    switch (sortBy) {
      case 'score':
        return (b.score || 0) - (a.score || 0);
      case 'user':
        return (a.user_email || 'Anonymous').localeCompare(b.user_email || 'Anonymous');
      case 'date':
      default:
        return (parseInt(b.timestamp) || 0) - (parseInt(a.timestamp) || 0);
    }
  });

  // Group attempts by user and assessment for management view
  const groupedAttempts = attempts.reduce((acc, attempt) => {
    const key = `${attempt.user_email}|${attempt.assessment_name}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(attempt);
    return acc;
  }, {});

  return (
    <div className="admin-program-management">
      {/* Tab Navigation */}
      <div className="card">
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button 
            className={`btn ${activeTab === 'analytics' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('analytics')}
          >
            📊 Analytics & Reports
          </button>
          <button 
            className={`btn ${activeTab === 'management' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('management')}
          >
            ⚙️ Attempt Management
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <h3>Filter Results</h3>
        <div className="form-row">
          <div className="form-group">
            <label>User Email:</label>
            <input
              type="email"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder="Filter by user email"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Assessment:</label>
            <input
              type="text"
              value={filterAssessment}
              onChange={(e) => setFilterAssessment(e.target.value)}
              placeholder="Filter by assessment name"
              className="form-control"
            />
          </div>
          {activeTab === 'analytics' && (
            <>
              <div className="form-group">
                <label>Status Filter:</label>
                <select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="form-control"
                >
                  <option value="all">All Attempts</option>
                  <option value="passed">Passed Only</option>
                  <option value="failed">Failed Only</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sort by:</label>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-control"
                >
                  <option value="date">Date (Newest First)</option>
                  <option value="score">Score (Highest First)</option>
                  <option value="user">User (A-Z)</option>
                </select>
              </div>
            </>
          )}
        </div>
        <button 
          className="btn btn-primary" 
          onClick={loadAttempts}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Apply Filters'}
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            setFilterEmail('');
            setFilterAssessment('');
            setFilterStatus('all');
            setSortBy('date');
          }}
          style={{marginLeft: '10px'}}
        >
          Clear Filters
        </button>
      </div>

      {loading && <div className="loading">Loading exam results...</div>}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && !loading && (
        <>
          {/* Summary Statistics */}
          <div className="card">
            <h3>Performance Analytics</h3>
            <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div className="stat-card" style={{ padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', textAlign: 'center', border: '1px solid #2196f3' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#1976d2', fontSize: '0.9em' }}>Total Attempts</h4>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#1976d2' }}>{totalAttempts}</div>
              </div>
              <div className="stat-card" style={{ padding: '15px', backgroundColor: '#e8f5e8', borderRadius: '8px', textAlign: 'center', border: '1px solid #4caf50' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#2e7d32', fontSize: '0.9em' }}>Passed Attempts</h4>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#2e7d32' }}>{passedAttempts}</div>
              </div>
              <div className="stat-card" style={{ padding: '15px', backgroundColor: '#ffebee', borderRadius: '8px', textAlign: 'center', border: '1px solid #f44336' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#c62828', fontSize: '0.9em' }}>Failed Attempts</h4>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#c62828' }}>{failedAttempts}</div>
              </div>
              <div className="stat-card" style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px', textAlign: 'center', border: '1px solid #9c27b0' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#7b1fa2', fontSize: '0.9em' }}>Pass Rate</h4>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#7b1fa2' }}>
                  {totalAttempts > 0 ? `${((passedAttempts / totalAttempts) * 100).toFixed(1)}%` : 'N/A'}
                </div>
              </div>
              <div className="stat-card" style={{ padding: '15px', backgroundColor: '#fff3e0', borderRadius: '8px', textAlign: 'center', border: '1px solid #ff9800' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#f57c00', fontSize: '0.9em' }}>Average Score</h4>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#f57c00' }}>{averageScore}%</div>
              </div>
              <div className="stat-card" style={{ padding: '15px', backgroundColor: '#e0f2f1', borderRadius: '8px', textAlign: 'center', border: '1px solid #009688' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#00695c', fontSize: '0.9em' }}>Unique Users</h4>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#00695c' }}>{uniqueUsers.length}</div>
              </div>
            </div>

            {/* User Summary */}
            <div style={{ marginBottom: '20px' }}>
              <h4>User Certification Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
                <div style={{ padding: '10px', backgroundColor: '#e8f5e8', borderRadius: '5px' }}>
                  <strong style={{ color: '#2e7d32' }}>Users Certified: {passedUsers}</strong>
                </div>
                <div style={{ padding: '10px', backgroundColor: '#ffebee', borderRadius: '5px' }}>
                  <strong style={{ color: '#c62828' }}>Users Not Certified: {failedUsers}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Attempts Table */}
          <div className="card">
            <h3>Detailed Exam Results ({filteredAttempts.length} results)</h3>
            {filteredAttempts.length === 0 ? (
              <p>No exam attempts found matching the selected filters.</p>
            ) : (
              <div className="table-responsive">
                <table className="table" style={{ fontSize: '0.9em' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th>User Email</th>
                      <th>Assessment</th>
                      <th>Score</th>
                      <th>Questions</th>
                      <th>Date & Time</th>
                      <th>Status</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAttempts.map((attempt, index) => {
                      const passed = attempt.passed;
                      const date = attempt.timestamp ? new Date(parseInt(attempt.timestamp) * 1000) : null;
                      return (
                        <tr key={index} style={{ backgroundColor: passed ? '#f8fff8' : '#fff8f8' }}>
                          <td style={{ fontWeight: 'bold' }}>{attempt.user_email || 'Anonymous'}</td>
                          <td>{attempt.assessment_name || 'Unknown'}</td>
                          <td style={{ fontWeight: 'bold', color: passed ? '#2e7d32' : '#c62828' }}>
                            {(attempt.score || 0).toFixed(1)}%
                          </td>
                          <td>{attempt.correct || 0} / {attempt.total || 0}</td>
                          <td>
                            {date ? (
                              <>
                                <div>{date.toLocaleDateString()}</div>
                                <div style={{ fontSize: '0.8em', color: '#666' }}>{date.toLocaleTimeString()}</div>
                              </>
                            ) : 'N/A'}
                          </td>
                          <td>
                            <span 
                              style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.8em',
                                fontWeight: 'bold',
                                backgroundColor: passed ? '#e8f5e8' : '#ffebee',
                                color: passed ? '#2e7d32' : '#c62828',
                                border: `1px solid ${passed ? '#4caf50' : '#f44336'}`
                              }}
                            >
                              {passed ? '✓ PASSED' : '✗ FAILED'}
                            </span>
                          </td>
                          <td>
                            <div style={{ fontSize: '0.8em' }}>
                              {(attempt.score || 0) >= 90 ? '🏆 Excellent' :
                               (attempt.score || 0) >= 80 ? '🎯 Good' :
                               (attempt.score || 0) >= 70 ? '✅ Pass' :
                               (attempt.score || 0) >= 60 ? '⚠️ Close' : '❌ Needs Improvement'}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Individual User Performance */}
          <div className="card">
            <h3>Individual User Performance</h3>
            <div className="table-responsive">
              <table className="table" style={{ fontSize: '0.9em' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th>User Email</th>
                    <th>Total Attempts</th>
                    <th>Best Score</th>
                    <th>Certification Status</th>
                    <th>Latest Attempt</th>
                  </tr>
                </thead>
                <tbody>
                  {uniqueUsers.sort((a, b) => b.bestScore - a.bestScore).map((userResult, index) => {
                    const latestAttempt = userResult.attempts.sort((a, b) => (parseInt(b.timestamp) || 0) - (parseInt(a.timestamp) || 0))[0];
                    const latestDate = latestAttempt?.timestamp ? new Date(parseInt(latestAttempt.timestamp) * 1000) : null;
                    return (
                      <tr key={index} style={{ backgroundColor: userResult.passed ? '#f8fff8' : '#fff8f8' }}>
                        <td style={{ fontWeight: 'bold' }}>{userResult.email}</td>
                        <td>{userResult.totalAttempts}</td>
                        <td style={{ fontWeight: 'bold', color: userResult.passed ? '#2e7d32' : '#c62828' }}>
                          {userResult.bestScore.toFixed(1)}%
                        </td>
                        <td>
                          <span 
                            style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              backgroundColor: userResult.passed ? '#e8f5e8' : '#ffebee',
                              color: userResult.passed ? '#2e7d32' : '#c62828',
                              border: `1px solid ${userResult.passed ? '#4caf50' : '#f44336'}`
                            }}
                          >
                            {userResult.passed ? '✓ CERTIFIED' : '✗ NOT CERTIFIED'}
                          </span>
                        </td>
                        <td>
                          {latestDate ? (
                            <>
                              <div>{latestDate.toLocaleDateString()}</div>
                              <div style={{ fontSize: '0.8em', color: '#666' }}>{latestDate.toLocaleTimeString()}</div>
                            </>
                          ) : 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Management Tab */}
      {activeTab === 'management' && !loading && (
        <div className="card">
          <div className="card-header">
            <h3>User Attempt Management ({Object.keys(groupedAttempts).length} users)</h3>
            <button 
              className="btn btn-secondary" 
              onClick={loadAttempts}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {Object.keys(groupedAttempts).length === 0 && (
            <p>No attempts found.</p>
          )}

          {Object.keys(groupedAttempts).length > 0 && (
            <div className="programs-list">
              {Object.entries(groupedAttempts).map(([key, userAttempts]) => {
                const [userEmail, assessmentName] = key.split('|');
                const latestAttempt = userAttempts[0];
                const attemptCount = userAttempts.length;

                return (
                  <div key={key} className="program-item">
                    <div className="program-header">
                      <div>
                        <h4>{userEmail}</h4>
                        <p style={{margin: '5px 0', color: '#666'}}>
                          Assessment: <strong>{assessmentName}</strong>
                        </p>
                      </div>
                      <div className="program-actions">
                        <button 
                          className="btn btn-danger" 
                          onClick={() => handleResetAttempt(userEmail, assessmentName)}
                          disabled={loading}
                        >
                          Reset Attempt
                        </button>
                      </div>
                    </div>
                    
                    <div className="program-details">
                      <p><strong>Attempts:</strong> {attemptCount}</p>
                      <p><strong>Latest Score:</strong> {(latestAttempt.score || 0).toFixed(1)}%</p>
                      <p><strong>Result:</strong> 
                        <span className={latestAttempt.passed ? 'status active' : 'status inactive'}>
                          {latestAttempt.passed ? 'PASSED' : 'FAILED'}
                        </span>
                      </p>
                      <p><strong>Correct Answers:</strong> {latestAttempt.correct || 0} / {latestAttempt.total || 0}</p>
                      <p><strong>Last Attempt:</strong> {formatDate(latestAttempt.timestamp)}</p>
                      
                      {attemptCount > 1 && (
                        <details style={{marginTop: '10px'}}>
                          <summary style={{cursor: 'pointer', color: '#007bff'}}>
                            View all {attemptCount} attempts
                          </summary>
                          <div style={{marginTop: '10px', paddingLeft: '20px'}}>
                            {userAttempts.map((attempt, idx) => (
                              <div key={idx} style={{
                                padding: '8px',
                                marginBottom: '5px',
                                background: '#f8f9fa',
                                borderRadius: '3px'
                              }}>
                                <strong>Attempt {idx + 1}:</strong> {(attempt.score || 0).toFixed(1)}% 
                                ({attempt.passed ? 'PASSED' : 'FAILED'}) - {formatDate(attempt.timestamp)}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="card" style={{background: '#fff3cd', border: '1px solid #ffc107'}}>
        <h4 style={{color: '#856404'}}>ℹ️ About Exam Results & Management</h4>
        <p style={{color: '#856404', margin: '10px 0'}}>
          <strong>Analytics Tab:</strong> View comprehensive performance reports, pass rates, and user certification status.
        </p>
        <p style={{color: '#856404', margin: '10px 0'}}>
          <strong>Management Tab:</strong> Reset user attempts to allow retaking assessments. 
          Users can only take each assessment once unless reset by an administrator.
        </p>
        <p style={{color: '#856404', margin: '10px 0'}}>
          <strong>Reset Effect:</strong> Resetting will delete all attempt records for that user and assessment, 
          allowing them to start fresh as if they never took it.
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;